import { FC, ReactNode, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Box,
  Button,
  Typography,
  Chip
} from "@mui/material";
import { Edit, TaskAlt } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { Country } from "../../../../api/Api";
import { canEditCountry, canManageSignOff } from "../../../../auth/permissions";
import { useRoles } from "../../../../auth/useRoles";
import { Colors } from "../../../../theme";
import { EditCountryDialog } from "./editCountryDialog";
import { CountryFlag } from "../../../../components/countryFlag";

type Props = {
  specificCountry: Country;
};

const NOT_SET = "N/A";

const Field: FC<{ label: string; children: ReactNode; labelFlex?: string }> = ({
  label,
  children,
  labelFlex = "1"
}) => (
  <Box display="flex" gap={3}>
    <Typography flex={labelFlex} variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Box flex="2.5">{children}</Box>
  </Box>
);

const TextValue: FC<{ value?: string | null }> = ({ value }) => (
  <Typography variant="body2">{value || NOT_SET}</Typography>
);

const Section: FC<{ title: string; children: ReactNode }> = ({
  title,
  children
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
    <Typography variant="h5" sx={{ marginBottom: 2 }}>
      {title}
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {children}
    </Box>
  </Box>
);

/*
 * Same three-section shape as the counterparty and broker cards, with two differences that follow
 * from the spec rather than from oversight: there is no Agreement Type (board #50 removed it, an NDA
 * or framework agreement is now a per-party review on Legal), and there is a Mandate Granted field,
 * which the other two books do not carry on the root.
 *
 * The requested parties sit in their own row below. They are the requester's proposal from the
 * request form, not what Market Access decided: that lives on the Market Access lifecycle with its
 * own needed/not-needed toggle and name box per party.
 */
export const InfoCard: FC<Props> = ({ specificCountry }) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const roles = useRoles();
  const navigate = useNavigate();

  const showEdit = canEditCountry(roles);

  const signOffStatus = specificCountry.signOff?.status;
  const signOffButtonLabel =
    signOffStatus === "completed"
      ? "VIEW SIGN OFF"
      : signOffStatus === "in_progress"
        ? "SIGN OFF IN PROGRESS"
        : signOffStatus === "rejected"
          ? "SIGN OFF REJECTED"
          : "COMPLETE SIGN OFF";

  // A signatory VP holds no app role, so the entry has to appear for anyone once a sign-off exists,
  // not only for the people who can start one.
  const showSignOffEntry = canManageSignOff(roles) || Boolean(signOffStatus);

  const signOffButtonColor =
    signOffStatus === "completed"
      ? Colors.NICE_GREEN
      : signOffStatus === "in_progress" || signOffStatus === "rejected"
        ? Colors.MFT_ORANGE
        : undefined;

  // Only the parties the requester actually named. An unnamed one has nothing to say, and a column
  // of N/A reads as missing data rather than as "not proposed".
  const requestedParties = [
    {
      title: "Requested TSO",
      name: specificCountry.tsoName,
      contactName: specificCountry.tsoContactName,
      contactEmail: specificCountry.tsoContactEmail
    },
    {
      title: "Requested Exchange",
      name: specificCountry.exchangeName,
      contactName: specificCountry.exchangeContactName,
      contactEmail: specificCountry.exchangeContactEmail
    },
    {
      title: "Requested Clearing Bank",
      name: specificCountry.clearingBankName,
      contactName: specificCountry.clearingBankContactName,
      contactEmail: specificCountry.clearingBankContactEmail
    }
  ].filter((party) => Boolean(party.name));

  return (
    <>
      <Grid position="relative" container spacing={3} padding={1}>
        <Box
          sx={{
            position: { xs: "static", md: "absolute" },
            top: { md: "16px" },
            right: { md: "16px" },
            bottom: { md: "16px" },
            display: "flex",
            flexDirection: { xs: "row", md: "column" },
            justifyContent: { xs: "flex-end", md: "space-between" },
            flexWrap: "wrap",
            gap: 1,
            alignItems: { xs: "center", md: "flex-end" },
            zIndex: 1,
            marginBottom: { xs: 1, md: 0 },
            paddingX: { xs: 1, md: 0 }
          }}
        >
          {showEdit && (
            <Button
              endIcon={<Edit />}
              onClick={() => setEditDialogOpen(true)}
              variant="outlined"
            >
              EDIT
            </Button>
          )}
          {showSignOffEntry && (
            <Button
              endIcon={<TaskAlt />}
              onClick={() =>
                navigate(`/countries/country/${specificCountry.id}/signoff`)
              }
              variant="contained"
              sx={
                signOffButtonColor
                  ? {
                      backgroundColor: signOffButtonColor,
                      "&:hover": { backgroundColor: signOffButtonColor }
                    }
                  : undefined
              }
            >
              {signOffButtonLabel}
            </Button>
          )}
        </Box>
        <Grid size={{ xs: 12, md: 12 }}>
          <Card elevation={5}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" }
                }}
              >
                <Section title="Basic Information">
                  <Field label="Country">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CountryFlag countryName={specificCountry.countryName} />
                      <TextValue value={specificCountry.countryName} />
                    </Box>
                  </Field>
                  <Field label="Trading Team">
                    <TextValue value={specificCountry.tradingTeam} />
                  </Field>
                  <Field label="Contracting Entity">
                    <TextValue value={specificCountry.contractingEntity} />
                  </Field>
                  <Field label="MFT Contact Person">
                    <TextValue
                      value={specificCountry.contactPersonTradingTeam}
                    />
                  </Field>
                  <Field label="VP">
                    <TextValue value={specificCountry.vp?.displayName} />
                  </Field>
                </Section>

                <Section title="Trading Information">
                  <Field label="Product Type">
                    <TextValue value={specificCountry.productType} />
                  </Field>
                  <Field label="Venue">
                    <TextValue value={specificCountry.venue} />
                  </Field>
                  <Field label="Settlement Type">
                    <TextValue value={specificCountry.tradingSettlementType} />
                  </Field>
                  <Field label="Transaction Type">
                    <TextValue
                      value={
                        specificCountry.transactionType
                          ? specificCountry.transactionType === "one-off"
                            ? "One-off"
                            : "Recurring"
                          : undefined
                      }
                    />
                  </Field>
                </Section>

                <Section title="Deal & Risk Information">
                  <Field label="Priority">
                    <Chip
                      label={specificCountry.urgent ? "URGENT" : "Not urgent"}
                      size="small"
                      variant="filled"
                      sx={
                        specificCountry.urgent
                          ? { backgroundColor: "#D21919", color: "#FFF" }
                          : {}
                      }
                    />
                  </Field>
                  <Field label="Expected Deal Volume">
                    <TextValue
                      value={
                        specificCountry.dealVolume
                          ? `${specificCountry.dealVolume} ${specificCountry.unit || ""}`.trim()
                          : undefined
                      }
                    />
                  </Field>
                  <Field label="Mandate Granted">
                    <TextValue value={specificCountry.mandate ? "Yes" : "No"} />
                  </Field>
                </Section>
              </Box>

              {requestedParties.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    marginTop: 1
                  }}
                >
                  {requestedParties.map((party) => (
                    <Section key={party.title} title={party.title}>
                      <Field label="Name">
                        <TextValue value={party.name} />
                      </Field>
                      {party.contactName && (
                        <Field label="Contact Name">
                          <TextValue value={party.contactName} />
                        </Field>
                      )}
                      {party.contactEmail && (
                        <Field label="Contact Email">
                          <TextValue value={party.contactEmail} />
                        </Field>
                      )}
                    </Section>
                  ))}
                </Box>
              )}

              {specificCountry.businessRational && (
                <Box sx={{ marginTop: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{ marginBottom: 2, marginRight: "67px" }}
                  >
                    Business Rational
                  </Typography>
                  <Typography variant="body2">
                    {specificCountry.businessRational}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {showEdit && (
        <EditCountryDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          specificCountry={specificCountry}
        />
      )}
    </>
  );
};
